import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { formPageCopyEn } from "@patient-watch/i18n/webapp/en-GB/formPageCopy";
import { formPageCopyDe } from "@patient-watch/i18n/webapp/de-DE/formPageCopy";
import { formPageCopyDa } from "@patient-watch/i18n/webapp/da-DK/formPageCopy";
import { formPageCopyEnUs } from "@patient-watch/i18n/webapp/en-US/formPageCopy";
import { formPageCopyFr } from "@patient-watch/i18n/webapp/fr-FR/formPageCopy";
import { formPageCopyIt } from "@patient-watch/i18n/webapp/it-IT/formPageCopy";
import { formPageCopyEs } from "@patient-watch/i18n/webapp/es-ES/formPageCopy";

interface FormPageCopy {
    entryFor: (label: string) => string;
    next: string;
    back: string;
    /** Banner on a form page opened from the results to change answers already given. */
    changingAnswers: string;
    submit: string;
    startNow: string;
    startForm: string;
    startingForm: string;
    formSubmitted: string;
    errorsOnPage: (count: number) => string;
    invalidValue: string;
    /** Running total under a group of inputs that must add up to a fixed number. */
    groupTotal: (total: number, target: number) => string;
    /** Error when such a group does not add up, if the page rule has no message of its own. */
    groupMustTotal: (target: number) => string;
    /** Progress line of a group that must add up to a fixed number, such as "7 of 10 placed". */
    groupPlaced: (total: number, target: number) => string;
    /** How many are still to place, such as "3 left". */
    groupLeft: (n: number) => string;
    /** How many are over the target, such as "5 over". */
    groupOver: (n: number) => string;
    /** Shown when the group adds up exactly. */
    groupComplete: (target: number) => string;
    /** Accessible names of the plus and minus buttons of a count row. */
    countIncrease: (label: string) => string;
    countDecrease: (label: string) => string;
    /** Under a count row left blank. A blank is not taken as 0. */
    countRequired: string;
    /** Error when an answer is more than an earlier answer it may not exceed, if the page rule has no message of its own. */
    answerTooHigh: string;
    /** Error when a "None" or "Prefer not to say" box is ticked with other boxes, if the page rule has no message. */
    exclusiveConflict: string;
    /** A list where at least one box must be ticked has none ticked. */
    tickAtLeastOne: string;
    pageOf: (page: number, total: number) => string;
    /** Shown under a question pre-filled with the patient's answer from their last entry. */
    carriedNote: string;
    questionOf: (current: number, total: number) => string;
    questionsRange: (min: number, max: number) => string;
    questionsRangeOf: (min: number, max: number, total: number) => string;
    formatProgressLabel: (input: {
        min: number;
        max: number;
        total: number;
        pageNumber: number;
        pageTotal: number;
    }) => string;
}

const formPageCopy: Record<SupportedLocale, FormPageCopy> = {
    "en-GB": formPageCopyEn,
    "en-US": formPageCopyEnUs,
    "de-DE": formPageCopyDe,
    "da-DK": formPageCopyDa,
    "fr-FR": formPageCopyFr,
    "it-IT": formPageCopyIt,
    "es-ES": formPageCopyEs,
};

export function useFormPageCopy(): FormPageCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(formPageCopy, locale);
}

export function getFormPageCopy(locale: SupportedLocale): FormPageCopy {
    return pickAppCopy(formPageCopy, locale);
}
